import { useContext, useState } from 'react';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useStatsigClient } from '@statsig/react-bindings';

import { QUESTION_TYPES } from '@api/instant_survey';
import { GATES } from '@constants';
import { Announcement, Button, GPTIcon, OverviewIcon, toast } from '@utilities';

import {
  createQuestionKey,
  getNonInstructionQuestionCountBefore,
  getQuestionsWithScreenersFirst,
  hasInvalidSequentialMonadicQuestionLogicSources,
} from '../../../../utilities/helpers';
import { buildRenderList, getGroupIdFromDroppableId } from './utilities/helpers';
import {
  getPipingQuestionNumbersById,
  replacePipingMentionReferences,
} from '../../utilities/helpers';

import ModalLogicChangeConfirmation from './components/ModalLogicChangeConfirmation';
import Question from './components/Question';
import PreviewBanner from './components/PreviewBanner';
import QuestionGroupBlock from './components/QuestionGroupBlock';
import QuestionPlaceholder from './components/QuestionPlaceholder';
import SequentialMonadicBlock, {
  SEQUENTIAL_MONADIC_SUBQUESTION_DROPPABLE_PREFIX,
  SEQUENTIAL_MONADIC_SUBQUESTION_DROPPABLE_TYPE,
} from './components/SequentialMonadicBlock';
import { SurveyContext } from '../../../../utilities/context';

import rocketError from './assets/rocket-error.svg';

import styles from './_index.module.scss';

const SEQUENTIAL_MONADIC_LOGIC_REORDER_ERROR =
  'Moving this question will break existing monadic question logic. Update or remove the logic before moving this question.';

const QuestionnaireList = ({
  calculateQuestionNumbers,
  canPreviewSurvey,
  firstErrorRef,
  generationError,
  generationErrorMessage,
  isDetailedQuestionView,
  isVideoSurvey,
  onAcceptPreview,
  onCancelPreview,
  onCreateQuestion,
  onDuplicateQuestion,
  onEditQuestion,
  onOpenAIGenerator,
  onPreviewSurvey,
  onRemoveQuestion,
  onReprompt,
  pipingQuestionsMap,
  questions,
  showActions,
  showPreviewBanner,
}) => {
  const { isReadOnly, questionDisplayIndexById, setShowSurveyTemplateModal, survey, updateSurvey } =
    useContext(SurveyContext);
  const { permissions = {} } = useSelector((state) => state?.user?.data) || {};
  const { canAdvancedEditSurvey, canUseAIGenerationQuestionnaire } = permissions;
  const { client } = useStatsigClient();
  const showQuestionGroupBlocks = client.checkGate(GATES.QUESTION_GROUP_BLOCKS);
  const [questionBeingMoved, setQuestionBeingMoved] = useState(null);
  const [questionsRequiringLogicConfirmation, setQuestionsRequiringLogicConfirmation] =
    useState(null);

  // Build the collapsed render list (groups shown as single blocks). When the gate is off we render
  // flat, so surveys that already carry questionGroup data don't show group blocks to gate-off users.
  const renderList = buildRenderList(questions, { enableGroups: showQuestionGroupBlocks });

  const getSequentialMonadicQuestionIdFromDroppableId = ({ droppableId = '' }) => {
    const prefix = `${SEQUENTIAL_MONADIC_SUBQUESTION_DROPPABLE_PREFIX}-`;
    if (!droppableId.startsWith(prefix)) return null;
    return droppableId.slice(prefix.length);
  };

  const reorderSequentialMonadicSubQuestions = ({ destinationIndex, questionId, sourceIndex }) => {
    const updatedQuestions = survey.questions.map((surveyQuestion) => {
      if (String(surveyQuestion?.id) !== questionId) return surveyQuestion;

      const existingSubQuestions = surveyQuestion.subQuestions || [];
      const activeSubQuestions = existingSubQuestions.filter(({ _delete }) => !_delete);
      const deletedSubQuestions = existingSubQuestions.filter(({ _delete }) => _delete);
      const reorderedSubQuestions = [...activeSubQuestions];
      const [movedSubQuestion] = reorderedSubQuestions.splice(sourceIndex, 1);

      if (!movedSubQuestion) return surveyQuestion;

      reorderedSubQuestions.splice(destinationIndex, 0, movedSubQuestion);

      return {
        ...surveyQuestion,
        subQuestions: [...reorderedSubQuestions, ...deletedSubQuestions],
      };
    });

    if (
      hasInvalidSequentialMonadicQuestionLogicSources({
        questionLogic: survey?.questionLogic,
        questions: updatedQuestions,
      })
    ) {
      toast.error(SEQUENTIAL_MONADIC_LOGIC_REORDER_ERROR);
      return;
    }

    updateSurvey({ questions: updatedQuestions }, { markDirty: true });
  };

  /**
   * handleReorderWithinGroup
   * Both source and destination droppable are the same group droppable — reorder the group's
   * members. (Moving questions into/out of/between groups is done via the question menu, not drag.)
   */
  const handleReorderWithinGroup = ({ destinationIndex: dstIdx, groupId, sourceIndex: srcIdx }) => {
    const flatQuestions = survey.questions;
    const groupQuestions = flatQuestions.filter((q) => q.questionGroup === groupId);
    const nonGroupQuestions = flatQuestions.filter((q) => q.questionGroup !== groupId);

    const reordered = [...groupQuestions];
    const [moved] = reordered.splice(srcIdx, 1);
    reordered.splice(dstIdx, 0, moved);

    // Count how many non-group questions appear before this group in the original flat list.
    const firstGroupIndex = flatQuestions.findIndex((q) => q.questionGroup === groupId);
    const insertAt = flatQuestions
      .slice(0, firstGroupIndex)
      .filter((q) => q.questionGroup !== groupId).length;

    const updatedQuestions = [...nonGroupQuestions];
    updatedQuestions.splice(insertAt, 0, ...reordered);

    updateSurvey({ questions: updatedQuestions }, { markDirty: true });
  };

  const onDragEnd = (result) => {
    const destinationIndex = result?.destination?.index;
    const sourceIndex = result?.source?.index;

    if (!result?.destination) return;

    // ── sequential monadic sub-question reorder ─────────────────────
    if (result.type === SEQUENTIAL_MONADIC_SUBQUESTION_DROPPABLE_TYPE) {
      if (result.source.droppableId !== result.destination.droppableId) return;
      if (destinationIndex === sourceIndex) return;

      const questionId = getSequentialMonadicQuestionIdFromDroppableId({
        droppableId: result.source.droppableId,
      });
      if (!questionId) return;

      reorderSequentialMonadicSubQuestions({ destinationIndex, questionId, sourceIndex });
      return;
    }

    // ── within-group reorder ─────────────────────────────────────────
    // The group's inner droppable uses a distinct type
    const sourceGroupId = getGroupIdFromDroppableId(result.source.droppableId);
    if (sourceGroupId != null) {
      if (destinationIndex === sourceIndex) return;
      handleReorderWithinGroup({ destinationIndex, groupId: sourceGroupId, sourceIndex });
      return;
    }

    // ── main droppable to main droppable ─────────────────────────────
    // Reorder ungrouped questions and whole group blocks within the main list.
    if (result.source.droppableId !== result.destination.droppableId) return;
    if (destinationIndex === sourceIndex) return;

    // ── move a question or group block within the main list ────────────────────────────────────
    // The drag indices point at the rendered list, where a group is one block.
    // Move the whole block and translate those indices back to the flat question list.
    const sourceRenderItem = renderList[sourceIndex];
    const destinationRenderItem = renderList[destinationIndex];

    // 1. Identify the flat questions that are moving (single q or full group block).
    // Use createQuestionKey (persisted id → questionClientId) so unsaved questions with no backend
    // id are distinct — matching on raw id would treat every id-less question as the same one.
    const movingKeys = new Set(
      sourceRenderItem.type === 'group'
        ? sourceRenderItem.questions.map(createQuestionKey)
        : [createQuestionKey(sourceRenderItem.question)]
    );
    const isMoving = (q) => movingKeys.has(createQuestionKey(q));
    // 2. Split the list to either staying or moving
    const stationaryQuestions = survey.questions.filter((q) => !isMoving(q));
    const movingQuestions = survey.questions.filter(isMoving);

    // 3. Compute the flat insertion point
    // Find where the destination sits in the flat list. Because we already removed the
    // moving questions, subtract any of them that came before it so the index still lines up.
    const destinationFlatAnchor =
      destinationRenderItem.type === 'group'
        ? destinationRenderItem.flatIndexStart
        : destinationRenderItem.flatIndex;

    const movingQuestionsBeforeDestination = survey.questions
      .slice(0, destinationFlatAnchor)
      .filter(isMoving).length;

    const isDraggingDown = destinationIndex > sourceIndex;
    let insertAt = destinationFlatAnchor - movingQuestionsBeforeDestination;

    // If dragging down, must land after the destination block
    if (isDraggingDown) {
      const destinationBlockSize =
        destinationRenderItem.type === 'group' ? destinationRenderItem.questions.length : 1;
      insertAt += destinationBlockSize;
    }

    insertAt = Math.max(0, Math.min(insertAt, stationaryQuestions.length));

    // 4. Rebuild the flat array
    let updatedQuestions = [...stationaryQuestions];
    updatedQuestions.splice(insertAt, 0, ...movingQuestions);
    updatedQuestions = getQuestionsWithScreenersFirst(updatedQuestions);

    const updatedQuestionNumbers = calculateQuestionNumbers(updatedQuestions);
    const originalQuestionNumbers = calculateQuestionNumbers(survey.questions);
    const updatedPipingQuestionNumbers = getPipingQuestionNumbersById(updatedQuestions);
    const originalPipingQuestionNumbers = getPipingQuestionNumbersById(survey.questions);

    const hasCarryForwardQuestionChanges = updatedQuestions.some(({ id }) => {
      const carryForwardLogic = survey?.carryForwardLogic?.[id];
      if (!carryForwardLogic?.sourceSurveyQuestionId) return false;

      return (
        updatedQuestionNumbers[carryForwardLogic.sourceSurveyQuestionId] >
        updatedQuestionNumbers[id]
      );
    });

    if (hasCarryForwardQuestionChanges) {
      toast.error(
        'Moving this question will break existing carry forward logic. Update or remove the logic before moving this question.'
      );
      return;
    }

    if (
      hasInvalidSequentialMonadicQuestionLogicSources({
        questionLogic: survey?.questionLogic,
        questions: updatedQuestions,
      })
    ) {
      toast.error(SEQUENTIAL_MONADIC_LOGIC_REORDER_ERROR);
      return;
    }

    const hasLogicChanges = updatedQuestions.some(({ id }, index) => {
      const currentLogic = survey?.questionLogic[id];

      if (
        // only first two questions (ignoring Instructional Text) can disqualify
        currentLogic?.disqualify &&
        !canAdvancedEditSurvey &&
        getNonInstructionQuestionCountBefore(updatedQuestions, index) >= 2
      ) {
        return true;
      }

      if (
        currentLogic?.skip?.logic.some(
          // skip logic cannot reference questions that are after the current question
          ({ question }) =>
            question &&
            updatedQuestions.findIndex(({ id: questionId }) => question.id === questionId) > index
        )
      ) {
        return true;
      }

      if (currentLogic?.option) {
        const hasInvalidOptionLogic = Object.values(currentLogic.option).some((answerOption) =>
          answerOption?.logic?.some(
            // option logic cannot reference questions that are after the current question
            ({ question }) =>
              question &&
              updatedQuestions.findIndex(({ id: questionId }) => question.id === questionId) > index
          )
        );
        return hasInvalidOptionLogic;
      }

      return false;
    });

    const hasPipingQuestionChanges = updatedQuestions.some(({ id }) => {
      const children = pipingQuestionsMap.childrenByParent[id] || [];
      const hasChildrenAllAfterParent = children.every(
        ({ questionId }) =>
          updatedPipingQuestionNumbers[questionId] > updatedPipingQuestionNumbers[id]
      );
      const parents = pipingQuestionsMap.parentsByChild[id] || [];
      const hasParentsAllBeforeChild = parents.every(
        ({ questionId }) =>
          updatedPipingQuestionNumbers[questionId] < updatedPipingQuestionNumbers[id]
      );

      if (!hasParentsAllBeforeChild || !hasChildrenAllAfterParent) {
        return true;
      }
      return false;
    });

    const questionsWithResyncedPiping = updatedQuestions.map((question) => {
      const parents = pipingQuestionsMap.parentsByChild[question.id] || [];
      if (!parents.length) {
        return question;
      }

      const mentionReferenceMappings = parents.reduce((acc, { questionId: parentId }) => {
        const oldParentCanonicalQuestionNumber = originalPipingQuestionNumbers[parentId];
        const newParentCanonicalQuestionNumber = updatedPipingQuestionNumbers[parentId];
        const oldParentDisplayQuestionNumber = originalQuestionNumbers[parentId];
        const newParentDisplayQuestionNumber = updatedQuestionNumbers[parentId];

        if (
          !oldParentCanonicalQuestionNumber ||
          !newParentCanonicalQuestionNumber ||
          !oldParentDisplayQuestionNumber ||
          !newParentDisplayQuestionNumber
        ) {
          return acc;
        }

        acc.push({
          newCanonicalQuestionNumber: newParentCanonicalQuestionNumber,
          newDisplayQuestionNumber: newParentDisplayQuestionNumber,
          oldCanonicalQuestionNumber: oldParentCanonicalQuestionNumber,
          oldDisplayQuestionNumber: oldParentDisplayQuestionNumber,
        });
        return acc;
      }, []);

      const nextRichText = replacePipingMentionReferences({
        mappings: mentionReferenceMappings,
        value: question.richText,
      });
      const nextText = replacePipingMentionReferences({
        mappings: mentionReferenceMappings,
        value: question.text,
      });

      if (nextRichText === question.richText && nextText === question.text) {
        return question;
      }

      return {
        ...question,
        richText: nextRichText,
        text: nextText,
      };
    });

    if (hasLogicChanges || hasPipingQuestionChanges) {
      setQuestionBeingMoved(movingQuestions[0]);
      setQuestionsRequiringLogicConfirmation(questionsWithResyncedPiping);
    } else {
      updateSurvey({ questions: questionsWithResyncedPiping }, { markDirty: true });
    }
  };

  return (
    <>
      <div className={styles['questionnaire-list-container']}>
        {questions.length === 0 &&
          !isVideoSurvey &&
          canUseAIGenerationQuestionnaire &&
          !isReadOnly &&
          !generationError && (
            <Announcement
              className={styles['questionnaire-list-announcement']}
              elevation={1}
              header="Introducing AI Generated Questionnaires"
              icon={<GPTIcon />}
              text={
                <>
                  Writer&apos;s block? Get started with AI suggested questions. Try an{' '}
                  <span
                    className={styles['questionnaire-list-banner-link']}
                    onClick={onOpenAIGenerator}
                  >
                    AI Generated Questionnaire
                  </span>{' '}
                  now.
                </>
              }
              variant="ai"
            />
          )}
        {!isVideoSurvey && questions.length === 0 && !generationError && !isReadOnly && (
          <Announcement
            className={styles['questionnaire-list-announcement']}
            elevation={1}
            header="Questionnaire Templates"
            icon={<OverviewIcon />}
            text={
              <>
                Use one of our{' '}
                <span
                  className={styles['questionnaire-list-banner-link']}
                  onClick={() => setShowSurveyTemplateModal(true)}
                >
                  Questionnaire Templates
                </span>{' '}
                to quickly get started.
              </>
            }
            variant="info"
          />
        )}
        {(showPreviewBanner || generationError) && (
          <PreviewBanner
            generationError={generationError}
            generationErrorMessage={generationErrorMessage}
            onAcceptPreview={onAcceptPreview}
            onCancelPreview={onCancelPreview}
            onReprompt={onReprompt}
          />
        )}
        {generationError && !showPreviewBanner && (
          <div className={styles['questionnaire-list-ai-error']}>
            <img alt="Error" src={rocketError} />
            <p className={styles['questionnaire-list-ai-error-message']}>
              Houston, we have a problem
            </p>
            {generationErrorMessage && (
              <p className={styles['questionnaire-list-ai-error-detail']}>
                {generationErrorMessage}
              </p>
            )}
            {onReprompt && <Button onClick={onReprompt} text="Try Again" variant="ai" />}
          </div>
        )}
        {questions.length === 0 && !generationError && !isReadOnly && (
          <QuestionPlaceholder onCreateQuestion={onCreateQuestion} />
        )}
        {questions.length > 0 && !generationError && (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable">
              {(provided) => (
                <div
                  className={styles['selections']}
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {renderList.map((item, index) => {
                    // A group render-item is dragged as a single block; its inner questions
                    // are reordered by the group's own droppable inside QuestionGroupBlock.
                    if (item.type === 'group') {
                      const group = survey?.questionGroups?.[item.groupId] ?? {
                        id: item.groupId,
                        name: '',
                        randomizeOrder: false,
                      };
                      return (
                        <Draggable
                          draggableId={`question-group-block-${item.groupId}`}
                          index={index}
                          isDragDisabled={isReadOnly}
                          key={`question-group-block-${item.groupId}`}
                        >
                          {(provided) => (
                            <QuestionGroupBlock
                              canPreviewSurvey={canPreviewSurvey}
                              firstErrorRef={firstErrorRef}
                              flatIndexStart={item.flatIndexStart}
                              group={group}
                              isDetailedQuestionView={isDetailedQuestionView}
                              onDuplicateQuestion={onDuplicateQuestion}
                              onEditQuestion={onEditQuestion}
                              onPreviewSurvey={onPreviewSurvey}
                              onRemoveQuestion={onRemoveQuestion}
                              provided={provided}
                              questionLogic={survey?.questionLogic}
                              questions={item.questions}
                              selectedQuestions={questions}
                              setQuestionLogic={(questionLogic) =>
                                updateSurvey({ questionLogic }, { markDirty: true })
                              }
                              showActions={showActions}
                            />
                          )}
                        </Draggable>
                      );
                    }

                    const { flatIndex, question } = item;
                    // Use stable question.id for draggableId to prevent state bugs when reordering questions
                    const draggableId = `question-${
                      question.id ?? question.questionClientId ?? flatIndex
                    }`;
                    return (
                      <Draggable
                        draggableId={draggableId}
                        index={index}
                        isDragDisabled={isReadOnly}
                        key={draggableId}
                      >
                        {(provided) => {
                          return question.type !== QUESTION_TYPES.SEQUENTIAL_MONADIC.value ? (
                            <Question
                              canPreviewSurvey={canPreviewSurvey}
                              firstErrorRef={firstErrorRef}
                              // Question uses index as the flat position into survey.questions
                              index={flatIndex}
                              isDetailedQuestionView={isDetailedQuestionView}
                              key={draggableId}
                              // String(flatIndex + 1) covers ai generated questions which will not have a calculated display index
                              // this works so long as generated questions are limited to single cost questions
                              number={
                                questionDisplayIndexById[question.id] ?? String(flatIndex + 1)
                              }
                              onDuplicateQuestion={onDuplicateQuestion}
                              onEditQuestion={onEditQuestion}
                              onPreviewSurvey={onPreviewSurvey}
                              onRemoveQuestion={onRemoveQuestion}
                              provided={provided}
                              question={question}
                              questionLogic={survey?.questionLogic}
                              selectedQuestions={questions}
                              setQuestionLogic={(questionLogic) =>
                                updateSurvey({ questionLogic }, { markDirty: true })
                              }
                              showActions={showActions}
                            />
                          ) : (
                            <SequentialMonadicBlock
                              canPreviewSurvey={canPreviewSurvey}
                              firstErrorRef={firstErrorRef}
                              isDetailedQuestionView={isDetailedQuestionView}
                              onPreviewSurvey={onPreviewSurvey}
                              pipingQuestionsMap={pipingQuestionsMap}
                              provided={provided}
                              question={question}
                              questionLogic={survey?.questionLogic}
                              selectedQuestions={questions}
                              setQuestionLogic={(questionLogic) =>
                                updateSurvey({ questionLogic }, { markDirty: true })
                              }
                              showActions={showActions}
                            />
                          );
                        }}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>
      {questionsRequiringLogicConfirmation && (
        <ModalLogicChangeConfirmation
          calculateQuestionNumbers={calculateQuestionNumbers}
          onClose={() => setQuestionsRequiringLogicConfirmation(null)}
          pipingQuestionsMap={pipingQuestionsMap}
          questionBeingMoved={questionBeingMoved}
          questionLogic={survey?.questionLogic}
          questions={questionsRequiringLogicConfirmation}
          updateSurvey={updateSurvey}
        />
      )}
    </>
  );
};

QuestionnaireList.propTypes = {
  calculateQuestionNumbers: PropTypes.func.isRequired,
  canPreviewSurvey: PropTypes.bool.isRequired,
  firstErrorRef: PropTypes.object.isRequired,
  generationError: PropTypes.bool,
  generationErrorMessage: PropTypes.string,
  isDetailedQuestionView: PropTypes.bool.isRequired,
  isVideoSurvey: PropTypes.bool.isRequired,
  onAcceptPreview: PropTypes.func,
  onCancelPreview: PropTypes.func,
  onCreateQuestion: PropTypes.func.isRequired,
  onDuplicateQuestion: PropTypes.func.isRequired,
  onEditQuestion: PropTypes.func.isRequired,
  onOpenAIGenerator: PropTypes.func.isRequired,
  onPreviewSurvey: PropTypes.func.isRequired,
  onRemoveQuestion: PropTypes.func.isRequired,
  onReprompt: PropTypes.func,
  pipingQuestionsMap: PropTypes.object.isRequired,
  questions: PropTypes.array,
  showActions: PropTypes.bool,
  showPreviewBanner: PropTypes.bool,
};

export default QuestionnaireList;
